import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Session, User } from '@supabase/supabase-js';

interface AuthContextType {
  user: User | null;
  isAdmin: boolean;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const activeRef = useRef(true);
  const loadingFinishedRef = useRef(false);

  const finishLoading = useCallback(() => {
    if (!activeRef.current || loadingFinishedRef.current) return;
    loadingFinishedRef.current = true;
    setLoading(false);
  }, []);

  const checkAdmin = useCallback(async (userId: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .eq('role', 'admin')
        .limit(1);

      if (error) {
        console.error('[Auth] checkAdmin error:', error);
        return false;
      }

      return (data ?? []).length > 0;
    } catch (err) {
      console.error('[Auth] checkAdmin failed:', err);
      return false;
    }
  }, []);

  const applySession = useCallback(
    (session: Session | null) => {
      const currentUser = session?.user ?? null;
      setUser(currentUser);

      if (!currentUser) {
        setIsAdmin(false);
        finishLoading();
        return;
      }

      // Fire-and-forget: never await inside auth listeners
      checkAdmin(currentUser.id)
        .then(admin => {
          if (!activeRef.current) return;
          setIsAdmin(admin);
        })
        .finally(() => {
          finishLoading();
        });
    },
    [checkAdmin, finishLoading]
  );

  useEffect(() => {
    activeRef.current = true;
    loadingFinishedRef.current = false;
    setLoading(true);

    const safetyTimeout = window.setTimeout(() => {
      if (!activeRef.current) return;
      console.error('[Auth] Safety timeout reached, releasing loading state');
      finishLoading();
    }, 10000);

    // 1) Register listener first
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!activeRef.current) return;
      if (event === 'INITIAL_SESSION') return;
      applySession(session);
    });

    // 2) Restore stored session
    supabase.auth.getSession()
      .then(({ data, error }) => {
        if (!activeRef.current) return;

        if (error) {
          console.error('[Auth] getSession error:', error);
          setUser(null);
          setIsAdmin(false);
          finishLoading();
          return;
        }

        applySession(data.session ?? null);
      })
      .catch(err => {
        console.error('[Auth] Session init failed:', err);
        if (!activeRef.current) return;
        setUser(null);
        setIsAdmin(false);
        finishLoading();
      });

    return () => {
      activeRef.current = false;
      window.clearTimeout(safetyTimeout);
      subscription.unsubscribe();
    };
  }, [applySession, finishLoading]);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error?.message ?? null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setIsAdmin(false);
  };

  return (
    <AuthContext.Provider value={{ user, isAdmin, loading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
